import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { computed, type ComputedRef } from 'vue';
import type { MChartTagCategoryDto, MChartTagDto } from '@/ApiClient';

export const useMChartTags = defineStore('mchart-tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.mChartTag_GetMChartTagCategories,
    dto: 'MChartTagCategoryDto',
    create: api.mChartTag_CreateMChartTagCategory,
    delete: api.mChartTag_DeleteMChartTagCategory,
    update: api.mChartTag_UpdateMChartTagCategory,
    signalR: 'MChartTagUpdate',
  });
  const { loaded } = globalStoreBase;

  const any: ComputedRef<boolean> = computed(() => {
    return Boolean(loaded.value.length && loaded.value.find((tagGroup) => tagGroup.tags && tagGroup.tags.length > 0));
  });

  const sorted: ComputedRef<MChartTagCategoryDto[]> = computed(() => {
    return loaded.value
      .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
      .map((tagGroup) => ({
        ...tagGroup,
        tags: tagGroup.tags?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0),
      }));
  });

  const flattened: ComputedRef<MChartTagDto[]> = computed(() => {
    return loaded.value.flatMap((tagGroup) => tagGroup.tags || []);
  });

  const baseCategory = computed(() => loaded.value[0]);

  async function addNewTag(name: string, color: string, tagCategoryId: number) {
    const brandId = useUser().brandId;

    const tagWithTagCategoryId = {
      name,
      color,
      tagCategoryId,
    };

    const tag = await api.mChartTag_CreateMChartTag(brandId, tagWithTagCategoryId);

    const categoryIndex = loaded.value.findIndex((category) => category.id === tagCategoryId);

    if (categoryIndex > -1) {
      if (!loaded.value[categoryIndex].tags) loaded.value[categoryIndex].tags = [];

      loaded.value[categoryIndex].tags?.push(tag);
    }

    return tag;
  }

  async function updateTag(tagId: number, name: string, color: string) {
    const brandId = useUser().brandId;

    const tag = await api.mChartTag_UpdateMChartTag(brandId, tagId, { color, name });

    const categoryIndex = loaded.value.findIndex((category) => category.id === tag.tagCategoryId);
    if (categoryIndex > -1) {
      const tagIndexInCategory = loaded.value[categoryIndex].tags?.findIndex((tag) => tag.id === tagId);
      if (tagIndexInCategory !== undefined && tagIndexInCategory > -1) {
        loaded.value[categoryIndex].tags?.splice(tagIndexInCategory, 1, tag);
      }
    }

    return tag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;

    await api.mChartTag_DeleteMChartTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id == tagId));
    if (category) {
      const index = category.tags!.findIndex((t) => t.id == tagId);
      category.tags!.splice(index, 1);
    }
  }

  function idsToTags(ids: number[]) {
    const tags = [];

    for (const id of ids) {
      const tag = getTagById(id);

      if (tag) tags.push(tag);
    }

    return tags;
  }

  function getTagById(id: number): MChartTagDto | undefined {
    return flattened.value.find((x) => x.id == id);
  }

  function getCategoryById(id: number): MChartTagCategoryDto | undefined {
    return loaded.value.find((x) => x.id == id);
  }

  // One palette-order Map per store: pill components sort tag ids through this instead of every
  // cell instance building its own Map over the whole palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  return {
    ...globalStoreBase,
    any,
    sorted,
    orderIndexById,
    sortByOrder,
    flattened,
    baseCategory,
    addNewCategory: globalStoreBase.create,
    updateCategory: globalStoreBase.update,
    deleteCategory: globalStoreBase.delete,
    addNewTag,
    updateTag,
    deleteTag,
    idsToTags,
    getTagById,
    getCategoryById,
  };
});
